/* Copyright (c) 2020-present CloudIO Inc */

import {
  action,
  atom,
  emptyFunction,
  useStateXAtom,
  useStateXRef,
} from '@cloudio-saas/statex';
import {
  Button,
  Dialog,
  DialogContentText,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
}));

export interface AlertDialogProps {
  title: string;
  content: string;
  onOk: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClose?: (
    event: React.SyntheticEvent,
    reason: 'escapeKeyDown' | 'backdropClick' | 'cancelClick',
  ) => void;
}

const alertDialogRefPath = ['transient', 'alertDialog', 'ref'];

const alertDialogOpenAtom = atom({
  path: ['transient', 'alertDialog', 'open'],
  defaultValue: false,
});

export const alertDialogAction = action<AlertDialogProps>(
  ({ getRef, set }, props) => {
    const ref = getRef(alertDialogRefPath);
    if (ref) {
      ref.current = props;
      set(alertDialogOpenAtom, true);
    }
  },
);

function AlertDialog() {
  const [open, setOpen] = useStateXAtom(alertDialogOpenAtom);
  const platform = usePlatform();
  const ref = useStateXRef<AlertDialogProps>(alertDialogRefPath, {
    title: '',
    content: '',
    onOk: emptyFunction,
  });
  const classes = useStyles();
  const { title, content, onClose, onOk } = ref.current;

  if (!open) {
    return null;
  }

  const handleClose = (
    event: React.SyntheticEvent,
    reason: 'escapeKeyDown' | 'backdropClick' | 'cancelClick',
  ) => {
    event.stopPropagation();
    setOpen(false);
    onClose?.(event, reason);
  };

  const handleOk = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    platform.forceUpdater(() => {
      setOpen(false);
    });
    onOk?.(event);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      data-testid="io_dialog"
      sx={[{ bgcolor: 'transparent !important' }]}
    >
      <DialogTitle sx={{ marginRight: '60px' }}>
        {title}
        <IconButton
          data-testid="confirm-close"
          aria-label="close"
          className={classes.closeButton}
          onClick={(e) => handleClose(e, 'cancelClick')}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{content}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="confirm-cancel"
          autoFocus
          color="primary"
          onClick={(e) => handleClose(e, 'cancelClick')}
        >
          Cancel
        </Button>
        <Button data-testid="confirm-yes" color="inherit" onClick={handleOk}>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(AlertDialog);
